import { useCallback, useEffect, useState } from 'react';
import type { StoryboardInputRecord } from '@agentic-ivy/shared-types';
import { storyboardApi, type StoryboardInfo } from '@/services/storyboardApi';

export interface SourceProgress {
  sourceId: string;
  step: string;
  pct: number;
}

function useConversationEvent(name: string, conversationId: string, handler: (detail: Record<string, unknown>) => void) {
  useEffect(() => {
    const onEvent = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.conversationId === conversationId) handler(detail);
    };
    window.addEventListener(name, onEvent);
    return () => window.removeEventListener(name, onEvent);
  }, [name, conversationId, handler]);
}

export function useStoryboard(conversationId: string) {
  const [info, setInfo] = useState<StoryboardInfo>({ versions: [], current: null });
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    try {
      setInfo(await storyboardApi.get(conversationId));
    } catch (err) {
      console.error('[Storyboard] Failed to load storyboard', err);
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  useEffect(() => {
    setLoading(true);
    refetch();
  }, [refetch]);
  useConversationEvent('storyboard-updated', conversationId, refetch);

  return { info, loading, refetch };
}

/** Whether the project has a storyboard, and whether the first check has come back (until it has, `exists` is false). */
export function useStoryboardExists(conversationId: string): { exists: boolean; known: boolean } {
  const { info, loading } = useStoryboard(conversationId);
  return { exists: info.versions.length > 0, known: !loading };
}

export function useStoryboardInputs(conversationId: string) {
  const [inputs, setInputs] = useState<StoryboardInputRecord[]>([]);

  const refetch = useCallback(async () => {
    try {
      setInputs(await storyboardApi.listInputs(conversationId));
    } catch (err) {
      console.error('[Storyboard] Failed to load inputs', err);
    }
  }, [conversationId]);

  useEffect(() => {
    refetch();
  }, [refetch]);
  useConversationEvent('storyboard-input', conversationId, refetch);
  useConversationEvent('storyboard-updated', conversationId, refetch);

  return { inputs, refetch };
}

export function useSourceProgress(conversationId: string): SourceProgress[] {
  const [byId, setById] = useState<Record<string, SourceProgress>>({});
  const onProgress = useCallback((d: Record<string, unknown>) => {
    const sourceId = String(d.sourceId ?? '');
    if (!sourceId) return;
    setById((prev) => ({ ...prev, [sourceId]: { sourceId, step: String(d.step ?? ''), pct: Number(d.pct) || 0 } }));
  }, []);
  useConversationEvent('source-progress', conversationId, onProgress);
  return Object.values(byId);
}
